/** Trish intake + consultant goal chips — top business outcomes per industry. */
export const SITEGLUE_INDUSTRY_OPTIONS = [
  "Healthcare",
  "Dental & Orthodontics",
  "Legal Services",
  "Home Services",
  "Physical Therapy & Chiropractic",
  "Real Estate",
  "Financial Services & Insurance",
  "Education & Private Schools",
  "Nonprofits & Churches",
  "Retail & E-Commerce",
] as const;

export type SiteGlueIndustryOption = (typeof SITEGLUE_INDUSTRY_OPTIONS)[number];

/** Universal outcomes aligned with SiteGlue AI Employee capabilities today. */
export const SITEGLUE_UNIVERSAL_BUSINESS_OUTCOMES = [
  "Never Miss a Customer Inquiry",
  "Capture More Qualified Leads",
  "Book More Appointments & Meetings",
  "Provide 24/7 Customer Support",
  "Capture After-Hours Opportunities",
  "Route Conversations to the Right Team Member",
  "Automate Customer Communications",
  "Improve Customer Engagement",
  "Reduce Administrative Work",
  "Scale Your Business Without Hiring Additional Staff",
] as const;

export const SITEGLUE_INDUSTRY_BUSINESS_OUTCOMES: Record<
  SiteGlueIndustryOption,
  readonly string[]
> = {
  Healthcare: [
    "More patient appointments",
    "Fewer missed calls",
    "Increased patient retention",
    "Improved online reviews",
    "Higher website conversions",
    "Automated patient communications",
    "Reduced administrative costs",
    "Improved patient experience",
    "Increased referrals",
    "Scalable patient acquisition",
  ],
  "Dental & Orthodontics": [
    "More new patients",
    "Increased treatment acceptance",
    "Automated appointment scheduling",
    "Reduced no-shows",
    "Increased Google reviews",
    "Improved insurance inquiry handling",
    "Higher case values",
    "Improved patient follow-up",
    "Better online visibility",
    "Increased recurring revenue",
  ],
  "Legal Services": [
    "More qualified consultations",
    "Automated lead qualification",
    "Improved client intake",
    "Increased website conversions",
    "Reduced response times",
    "Better case acquisition",
    "Increased referral business",
    "Improved client communications",
    "Lower intake costs",
    "Increased revenue per attorney",
  ],
  "Home Services": [
    "More booked estimates",
    "Automated lead follow-up",
    "Increased emergency service calls",
    "Improved local search rankings",
    "Increased online reviews",
    "Better customer communication",
    "Reduced scheduling costs",
    "Increased repeat customers",
    "Improved technician utilization",
    "Scalable lead generation",
  ],
  "Physical Therapy & Chiropractic": [
    "Increased patient volume",
    "Automated appointment reminders",
    "Improved patient retention",
    "Higher referral rates",
    "Increased online visibility",
    "Better insurance pre-qualification",
    "Reduced front desk workload",
    "Increased treatment plan compliance",
    "Improved patient engagement",
    "Increased lifetime patient value",
  ],
  "Real Estate": [
    "More qualified buyer and seller leads",
    "Automated lead nurturing",
    "Increased property inquiries",
    "Improved response times",
    "Increased appointment bookings",
    "Higher conversion rates",
    "Automated follow-ups",
    "Improved market visibility",
    "Increased referrals",
    "Scalable lead generation",
  ],
  "Financial Services & Insurance": [
    "Increased qualified prospects",
    "Automated appointment scheduling",
    "Improved client communications",
    "Increased policy or service inquiries",
    "Improved lead nurturing",
    "Higher client retention",
    "Reduced administrative work",
    "Increased cross-selling opportunities",
    "Improved compliance workflows",
    "Scalable client acquisition",
  ],
  "Education & Private Schools": [
    "Increased student inquiries",
    "Automated admissions workflows",
    "Improved enrollment rates",
    "Increased campus tour bookings",
    "Better parent communications",
    "Improved application completion rates",
    "Increased retention",
    "Improved community engagement",
    "Scalable recruitment efforts",
    "Reduced administrative workload",
  ],
  "Nonprofits & Churches": [
    "Increased Donations",
    "Improved Volunteer Engagement",
    "Automated Member Communications",
    "Increased Event Registrations",
    "Improved Donor Retention",
    "Increased Community Engagement",
    "Automated Follow-Ups",
    "Better Visitor Conversions",
    "Increased Recurring Donations",
    "Scalable Outreach Programs",
  ],
  "Retail & E-Commerce": [
    "Increased online sales",
    "Reduced cart abandonment",
    "Automated customer support",
    "Increased repeat purchases",
    "Improved product recommendations",
    "Increased customer lifetime value",
    "Better email and SMS engagement",
    "Increased website conversions",
    "Improved customer satisfaction",
    "Scalable revenue growth",
  ],
};

const INDUSTRY_ALIASES: Record<string, SiteGlueIndustryOption> = {
  healthcare: "Healthcare",
  health: "Healthcare",
  medical: "Healthcare",
  clinic: "Healthcare",
  hospital: "Healthcare",
  dental: "Dental & Orthodontics",
  orthodontics: "Dental & Orthodontics",
  "dental & orthodontics": "Dental & Orthodontics",
  legal: "Legal Services",
  "legal services": "Legal Services",
  law: "Legal Services",
  attorney: "Legal Services",
  "home services": "Home Services",
  hvac: "Home Services",
  plumbing: "Home Services",
  "physical therapy & chiropractic": "Physical Therapy & Chiropractic",
  "physical therapy": "Physical Therapy & Chiropractic",
  chiropractic: "Physical Therapy & Chiropractic",
  chiro: "Physical Therapy & Chiropractic",
  pt: "Physical Therapy & Chiropractic",
  "real estate": "Real Estate",
  realtor: "Real Estate",
  "financial services & insurance": "Financial Services & Insurance",
  "financial services": "Financial Services & Insurance",
  finance: "Financial Services & Insurance",
  insurance: "Financial Services & Insurance",
  "education & private schools": "Education & Private Schools",
  education: "Education & Private Schools",
  school: "Education & Private Schools",
  university: "Education & Private Schools",
  college: "Education & Private Schools",
  "nonprofits & churches": "Nonprofits & Churches",
  nonprofit: "Nonprofits & Churches",
  "nonprofit / faith": "Nonprofits & Churches",
  "nonprofit/faith": "Nonprofits & Churches",
  "nonprofits and churches": "Nonprofits & Churches",
  church: "Nonprofits & Churches",
  faith: "Nonprofits & Churches",
  ministry: "Nonprofits & Churches",
  "retail & e-commerce": "Retail & E-Commerce",
  retail: "Retail & E-Commerce",
  "e-commerce": "Retail & E-Commerce",
  ecommerce: "Retail & E-Commerce",
};

/** Map free text or legacy labels to a canonical Trish industry option. */
export function resolveSiteGlueIndustry(input?: string | null): SiteGlueIndustryOption | null {
  const trimmed = (input ?? "").trim();
  if (!trimmed) return null;

  const exact = SITEGLUE_INDUSTRY_OPTIONS.find(
    (option) => option.toLowerCase() === trimmed.toLowerCase(),
  );
  if (exact) return exact;

  const key = trimmed.toLowerCase();
  if (INDUSTRY_ALIASES[key]) return INDUSTRY_ALIASES[key];

  const aliasesBySpecificity = Object.entries(INDUSTRY_ALIASES).sort(
    ([a], [b]) => b.length - a.length,
  );
  for (const [alias, industry] of aliasesBySpecificity) {
    if (key.includes(alias)) return industry;
  }

  return null;
}

/**
 * Top 10 business outcomes for the selected industry.
 * Unknown / general orgs get universal (non-healthcare) outcomes — never assume medical.
 */
export function getSiteGlueIndustryBusinessOutcomes(industry?: string | null): string[] {
  const resolved = resolveSiteGlueIndustry(industry);
  if (resolved) return [...SITEGLUE_INDUSTRY_BUSINESS_OUTCOMES[resolved]];
  return [...SITEGLUE_UNIVERSAL_BUSINESS_OUTCOMES];
}
